import React, { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import { DotLottleLoader } from '../Loader';
import { AttributeOptionsSelector } from './AttributeOptionsSelector';

interface Props {
  title: string;
  // Same path for GET (current ids) and PUT (new id list):
  // /admin/<entity>/<id>/attribute-values
  endpoint: string;
  onClose: () => void;
  onSaved?: () => void;
}

// "Assign attribute options" modal used by entities without a full edit form
// (sub-professions, areas of focus). Loads the current selection on open and
// PUTs the whole list on save (the backend replaces the vector + enqueues a
// recompute).
export const AttributeOptionsModal: FC<Props> = ({ title, endpoint, onClose, onSaved }) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axiosInstant
      .get(endpoint)
      .then((r) => {
        if (active) setSelectedIds(r.data?.attribute_value_ids ?? []);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load attribute options');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [endpoint]);

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstant.put(endpoint, { attribute_value_ids: selectedIds });
      toast.success('Attribute options saved — scores will recompute');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save attribute options');
    }
    setSaving(false);
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Typography fontFamily="CircularStdBlack" sx={{ fontSize: 22 }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box className="flex justify-center py-6">
            <DotLottleLoader sx={{ width: 48, height: 48 }} fill={false} />
          </Box>
        ) : (
          <AttributeOptionsSelector selectedIds={selectedIds} onChange={setSelectedIds} />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
